import React from "react";
import { cn } from "../../lib/utils";

interface CommandProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export function Command({ children, className, ...props }: CommandProps) {
  return (
    <div
      className={cn(
        "flex h-full w-full flex-col overflow-hidden rounded-md bg-white",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

interface CommandInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  onValueChange?: (value: string) => void;
}

export function CommandInput({
  className,
  onValueChange,
  ...props
}: CommandInputProps) {
  return (
    <div className="flex items-center border-b px-3">
      <input
        className={cn(
          "flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-gray-500 disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        onChange={(e) => {
          props.onChange?.(e);
          onValueChange?.(e.target.value);
        }}
        {...props}
      />
    </div>
  );
}

interface CommandListProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export function CommandList({
  children,
  className,
  ...props
}: CommandListProps) {
  return (
    <div
      className={cn(
        "max-h-[200px] overflow-y-auto overflow-x-hidden",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function CommandEmpty({ children }: { children: React.ReactNode }) {
  return (
    <div className="py-6 text-center text-sm text-gray-500">{children}</div>
  );
}

export function CommandGroup({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn("overflow-hidden p-1", className)}>{children}</div>;
}

interface CommandItemProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  selected?: boolean;
}

interface CommandItemProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  onItemSelect?: (value: string) => void;
  value?: string;
  selected?: boolean;
}

export function CommandItem({
  children,
  className,
  selected,
  onItemSelect,
  value,
  ...props
}: CommandItemProps) {
  return (
    <button
      className={cn(
        "relative flex w-full cursor-pointer select-none items-center rounded-md px-2 py-1.5 text-sm outline-none",
        "hover:bg-gray-100 focus:bg-gray-100",
        selected && "bg-gray-100",
        className
      )}
      onClick={(e) => {
        props.onClick?.(e);
        if (onItemSelect && value) {
          onItemSelect(value);
        }
      }}
      {...props}
    >
      {children}
    </button>
  );
}
